import React, { useMemo, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { LayoutBreakpoints } from '@/constants/LayoutBreakpoints';
import { useIsWidthUp } from '~/global/hooks/useIsWidthUp';
import { db } from '@/lib/local-api';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import type {
  WorkoutRecord,
  WorkoutExerciseDimensionRecord,
  WorkoutSetValueRecord,
} from '~/powersync/generated/schema';
import { useWorkoutSetSequence } from '~/workout/hooks/useWorkoutSetSequence';
import {
  findActiveSet,
  findNextIncomplete,
} from '~/workout/utils/findActiveSet';
import { SetListRow } from '~/workout/components/set-list-row/SetListRow';
import { ActiveSet } from '~/workout/components/active-set/ActiveSet';
import { WorkoutBuilderHeader } from '~/workout/components/workout-builder-header/WorkoutBuilderHeader';

interface WorkoutProgressViewSyncedProps {
  /** The (started) workout record */
  workout: WorkoutRecord;
  /** Additional CSS classes for the root container */
  className?: string;
}

const WorkoutProgressViewSynced: React.FC<WorkoutProgressViewSyncedProps> = ({
  workout,
  className,
}) => {
  const { t } = useTranslation('workout');
  const powerSync = usePowerSync();
  const workoutId = workout.id;
  const isDesktop = useIsWidthUp(LayoutBreakpoints.tablet);

  const { sequence, isLoading, error } = useWorkoutSetSequence(workoutId);

  // Per-exercise dimensions and per-set values to render the compact meta line.
  const { data: dimensions } =
    useLivePowerSyncQuery<WorkoutExerciseDimensionRecord>(
      db.Workout.queries.findDimensionsByWorkout(workoutId),
      [workoutId],
    );
  const { data: setValues } = useLivePowerSyncQuery<WorkoutSetValueRecord>(
    db.Workout.queries.findSetValuesByWorkout(workoutId),
    [workoutId],
  );

  // User-selected set. When null, selection falls back to the first
  // non-completed set at render time (so the default tracks live data).
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const completeSetMutation = useMutation({
    mutationFn: (setId: string) =>
      db.Workout.mutations.completeSet(powerSync, setId),
  });
  const uncompleteSetMutation = useMutation({
    mutationFn: (setId: string) =>
      db.Workout.mutations.uncompleteSet(powerSync, setId),
  });

  const dimensionsByExercise = useMemo(() => {
    const map = new Map<string, WorkoutExerciseDimensionRecord[]>();
    for (const dimension of dimensions ?? []) {
      if (!dimension.exerciseId) continue;
      const list = map.get(dimension.exerciseId) ?? [];
      list.push(dimension);
      map.set(dimension.exerciseId, list);
    }
    return map;
  }, [dimensions]);

  const valuesBySet = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    for (const value of setValues ?? []) {
      if (!value.setId || !value.exerciseDimensionId) continue;
      const inner = map.get(value.setId) ?? new Map<string, number>();
      inner.set(value.exerciseDimensionId, value.value ?? 0);
      map.set(value.setId, inner);
    }
    return map;
  }, [setValues]);

  const formatMeta = (exerciseId: string, setId: string, setNumber: number) => {
    const dims = dimensionsByExercise.get(exerciseId) ?? [];
    const valuesStr = dims
      .map((dimension) => {
        const value = valuesBySet.get(setId)?.get(dimension.id) ?? 0;
        const unit =
          dimension.unit && dimension.unit.toUpperCase() !== 'NONE'
            ? dimension.unit
            : '';
        return `${value}${unit}`;
      })
      .join(' × ');
    const setLabel = t('progress.setShort', { number: setNumber });
    return valuesStr ? `${setLabel} • ${valuesStr}` : setLabel;
  };

  if (isLoading) {
    return (
      <View className={cn('flex-1 bg-background p-4', className)}>
        <Text className="text-center text-muted-foreground">
          {t('sequence.loading')}
        </Text>
      </View>
    );
  }

  if (error || sequence.length === 0) {
    return (
      <View className={cn('flex-1 bg-background p-4', className)}>
        <Text className="text-center text-muted-foreground">
          {error ? t('sequence.error') : t('sequence.empty')}
        </Text>
      </View>
    );
  }

  const activeItem = findActiveSet(sequence);
  const selectedItem =
    (selectedKey
      ? sequence.find((item) => item.key === selectedKey)
      : undefined) ??
    activeItem ??
    sequence[sequence.length - 1];

  const completedCount = sequence.filter((item) => item.set.completedAt).length;
  const setsInExercise = sequence.filter(
    (item) => item.exercise.id === selectedItem.exercise.id,
  ).length;

  const handleToggleComplete = (setId: string, completed: boolean) => {
    if (completed) {
      uncompleteSetMutation.mutate(setId);
    } else {
      completeSetMutation.mutate(setId);
    }
  };

  const handleCompleteSelected = () => {
    completeSetMutation.mutate(selectedItem.set.id);
    const next = findNextIncomplete(sequence, selectedItem.key);
    setSelectedKey(next ? next.key : null);
  };

  const setsHeader = (
    <View className="flex-row items-center justify-between">
      <Text className="font-sora-semibold text-base text-text-primary">
        {t('progress.setsTitle')}
      </Text>
      <Text className="text-sm font-medium text-text-secondary">
        {t('progress.setsCompleted', {
          done: completedCount,
          total: sequence.length,
        })}
      </Text>
    </View>
  );

  const setsList = (
    <ScrollView
      className="flex-1"
      contentContainerClassName="gap-2 pb-4"
      showsVerticalScrollIndicator={false}
    >
      {sequence.map((item) => (
        <SetListRow
          key={item.key}
          item={item}
          isSelected={item.key === selectedItem.key}
          meta={formatMeta(item.exercise.id, item.set.id, item.setNumber)}
          onSelect={() => setSelectedKey(item.key)}
          onToggleComplete={() =>
            handleToggleComplete(item.set.id, Boolean(item.set.completedAt))
          }
        />
      ))}
    </ScrollView>
  );

  const activeCard = (
    <ActiveSet.Synced
      setItem={selectedItem}
      workoutId={workoutId}
      label={{ index: selectedItem.setNumber, total: setsInExercise }}
      onCompleteSet={handleCompleteSelected}
    />
  );

  return (
    <View className={cn('flex-1 bg-background', className)}>
      <WorkoutBuilderHeader.Body
        title={workout.name || t('progress.title')}
        workout={workout}
        className="px-4 pt-4 md:hidden"
      />

      <View className="flex-1 md:flex-row md:gap-8 md:p-8">
        {/* Active set card — left column on desktop. Conditionally rendered
            (not `display:none` via `hidden md:flex`) to avoid the iOS New Arch
            phone-`display:none` re-render crash (RN #52349). */}
        {isDesktop && (
          <View className="flex-1">
            <View className="rounded-2xl bg-surface p-6">{activeCard}</View>
          </View>
        )}

        {/* Sets list — right column on desktop */}
        <View className="flex-1">
          <View className="px-4 pb-3 pt-4 md:px-0 md:pt-0">{setsHeader}</View>
          <View className="flex-1 px-4 md:px-0">{setsList}</View>
        </View>

        {/* Active set card — fixed bottom bar on mobile */}
        {!isDesktop && (
          <View className="border-t border-border bg-background px-5 pb-4 pt-3">
            {activeCard}
          </View>
        )}
      </View>
    </View>
  );
};

export const WorkoutProgressView = {
  Synced: WorkoutProgressViewSynced,
};
